import axios from 'axios';
import { getToken, redirectToLogin } from '../../utils/auth';

const DEFAULT_GET_CACHE_TTL_MS = 5000;
const getResponseCache = new Map();
const inFlightGetRequests = new Map();

const buildCacheKey = (url, config = {}) => {
  const params = config?.params ? JSON.stringify(config.params) : '';
  return `${url}::${params}`;
};

const getBaseURL = () => {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5058/api';
  }

  const protocol = window.location.protocol;
  const domain = process.env.REACT_APP_API_BASE_URL.replace(/^https?:\/\//, '');
  return `${protocol}//${domain}/api`;
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const originalGet = api.get.bind(api);

api.get = (url, config = {}) => {
  if (config?.skipCache) {
    return originalGet(url, config);
  }

  const cacheKey = buildCacheKey(url, config);
  const ttlMs =
    typeof config?.cacheTtlMs === 'number' ? config.cacheTtlMs : DEFAULT_GET_CACHE_TTL_MS;
  const now = Date.now();
  const cachedEntry = getResponseCache.get(cacheKey);

  if (cachedEntry && now - cachedEntry.timestamp < ttlMs) {
    return Promise.resolve(cachedEntry.response);
  }

  const pendingRequest = inFlightGetRequests.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = originalGet(url, config)
    .then((response) => {
      getResponseCache.set(cacheKey, {
        response,
        timestamp: Date.now()
      });
      inFlightGetRequests.delete(cacheKey);
      return response;
    })
    .catch((error) => {
      inFlightGetRequests.delete(cacheKey);
      throw error;
    });

  inFlightGetRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

api.clearGetCache = (matcher) => {
  const keys = Array.from(getResponseCache.keys());

  keys.forEach((key) => {
    if (!matcher || key.includes(matcher)) {
      getResponseCache.delete(key);
    }
  });
};

export default api;
